import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Mail, Loader2, Send, History, FileText, ToggleLeft, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import TemplateList from './admin-email/TemplateList';
import TemplateEditor from './admin-email/TemplateEditor';
import AutomationTab from './admin-email/AutomationTab';
import ManualSendTab from './admin-email/ManualSendTab';
import LogsTab from './admin-email/LogsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { id: 'templates', label: 'Templates', icon: FileText },
  { id: 'automation', label: 'Automation', icon: ToggleLeft },
  { id: 'send', label: 'Send Email', icon: Send },
  { id: 'logs', label: 'Logs', icon: History },
];

const AdminEmailTemplates = () => {
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [standardizing, setStandardizing] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/email-templates`);
      setTemplates(res.data.templates || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const standardizeLayouts = async () => {
    setStandardizing(true);
    try {
      const response = await axios.post(`${API}/admin/email-templates/standardize-layout`);
      toast.success(`${response.data.updated_templates} templates updated to the standard layout`);
      fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not standardize template layouts');
    } finally { setStandardizing(false); }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-email-page">
      <div className="flex items-center gap-3 mb-6">
        <Mail className="h-7 w-7 text-green-600" />
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Email Center</h1>
          <p className="text-sm text-gray-500">
            Manage branded templates, automation triggers, and manual sends.
          </p>
        </div>
        <button
          onClick={fetchTemplates}
          className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
          title="Refresh templates"
          data-testid="email-refresh-btn"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
        <button
          onClick={standardizeLayouts}
          disabled={standardizing}
          className="text-xs text-green-700 hover:text-green-900 flex items-center gap-1 disabled:opacity-50"
          data-testid="standardize-email-layouts-button"
        >
          <FileText className="h-3.5 w-3.5" /> {standardizing ? 'Updating…' : 'Apply standard layout'}
        </button>
      </div>

      <div className="flex gap-1 border-b mb-6 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeTab === t.id
                ? 'border-green-600 text-green-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`email-tab-${t.id}`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'templates' && (
        selectedKey ? (
          <TemplateEditor
            templateKey={selectedKey}
            onClose={() => { setSelectedKey(null); fetchTemplates(); }}
          />
        ) : (
          <TemplateList templates={templates} onSelect={(k) => setSelectedKey(k)} />
        )
      )}

      {activeTab === 'automation' && (
        <AutomationTab templates={templates} />
      )}

      {activeTab === 'send' && (
        <ManualSendTab templates={templates} />
      )}

      {activeTab === 'logs' && (
        <LogsTab templates={templates} />
      )}
    </div>
  );
};

export default AdminEmailTemplates;
